import React, { useEffect, useMemo, useState } from 'react'
import { Container, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Typography } from '@material-ui/core'
import { DataGrid, GridToolbar } from '@mui/x-data-grid'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'react-simple-snackbar'
import { useHistory } from 'react-router-dom'
import styles from '../Clients/Clients.module.css'
import useAuthUser from '../../hooks/useAuthUser'
import { deleteOrder, getOrdersByUser, updateOrder } from '../../actions/orderActions'

const STATUS_OPTIONS = ['Unpaid', 'Partial', 'Paid', 'Booked']
const PAYMENT_METHODS = ['Cash', 'Check', 'Credit Card', 'Bank Transfer', 'Others']

const currency = (val) => {
  const num = Number(val) || 0
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

const todayIso = () => new Date().toISOString().slice(0, 10)

const statusStyle = (status) => {
  const base = { borderRadius: 12, padding: '4px 10px', fontWeight: 600, fontSize: 12, color: '#0f172a' }
  if (status === 'Paid') return { ...base, backgroundColor: '#b0f2c2', color: '#065f46' }
  if (status === 'Partial') return { ...base, backgroundColor: '#cde5ff', color: '#0b4f91' }
  if (status === 'Booked') return { ...base, backgroundColor: '#fef3c7', color: '#92400e' }
  return { ...base, backgroundColor: '#fecdd3', color: '#991b1b' } // Unpaid/default
}

const viewButtonColor = (row) => (row?.link ? '#2563eb' : '#ef4444')

const Orders = () => {
  const auth = useAuthUser()
  const dispatch = useDispatch()
  const history = useHistory()
  const { orders = [], isLoading } = useSelector((state) => state.orders || {})
  const [open, setOpen] = useState(false)
  const [currentId, setCurrentId] = useState(null)
  const [formData, setFormData] = useState({ status: 'Unpaid', link: '', payments: [] })
  const [rowSelectionModel, setRowSelectionModel] = useState([])
  const [bulkStatus, setBulkStatus] = useState('Unpaid')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmBookedOpen, setConfirmBookedOpen] = useState(false)
  const [confirmOrders, setConfirmOrders] = useState([])
  const [deleteTarget, setDeleteTarget] = useState(null)
  // eslint-disable-next-line no-unused-vars
  const [openSnackbar, closeSnackbar] = useSnackbar()

  useEffect(() => {
    dispatch(getOrdersByUser())
  }, [dispatch])

  const rows = useMemo(() => {
    return [...orders].sort((a, b) => {
      const aDate = a.receivedByDate ? new Date(a.receivedByDate).getTime() : 0
      const bDate = b.receivedByDate ? new Date(b.receivedByDate).getTime() : 0
      return bDate - aDate
    })
  }, [orders])

  const paymentTotal = (payments = []) => (Array.isArray(payments) ? payments.reduce((sum, p) => sum + (Number(p.amountPaid) || 0), 0) : 0)

  const handleEdit = (row) => {
    setCurrentId(row?._id)
    setFormData({
      status: row?.status || 'Unpaid',
      link: row?.link || '',
      payments: Array.isArray(row?.payments) ? row.payments : [],
    })
    setOpen(true)
  }

  const handlePaymentChange = (idx, field, value) => {
    setFormData((prev) => {
      const next = Array.isArray(prev.payments) ? [...prev.payments] : []
      next[idx] = { ...next[idx], [field]: value }
      return { ...prev, payments: next }
    })
  }

  const handleAddPayment = () => {
    setFormData((prev) => ({
      ...prev,
      payments: [...(prev.payments || []), { amountPaid: 0, datePaid: todayIso(), paymentMethod: 'Check', note: '', paidBy: '' }],
    }))
  }

  const handleRemovePayment = (idx) => {
    setFormData((prev) => ({
      ...prev,
      payments: (prev.payments || []).filter((_, i) => i !== idx),
    }))
  }

  const handleSave = () => {
    if (!currentId) return
    dispatch(updateOrder(currentId, formData, openSnackbar))
    setOpen(false)
  }

  const handleDeleteClick = (row) => {
    setDeleteTarget(row)
  }

  const handleCancelDelete = () => {
    setDeleteTarget(null)
  }

  const handleConfirmDelete = () => {
    if (!deleteTarget?._id) return
    dispatch(deleteOrder(deleteTarget._id, openSnackbar))
    setRowSelectionModel((prev) => prev.filter((id) => id !== deleteTarget._id))
    setDeleteTarget(null)
  }

  const columns = [
    { field: 'invoiceNumber', headerName: 'Invoice #', width: 160 },
    { field: 'pcInvoiceNumber', headerName: 'PC Invoice #', width: 160 },
    { field: 'clientPONumber', headerName: 'Client PO #', width: 160 },
    {
      field: 'receivedByDate',
      headerName: 'Ship Date',
      width: 140,
      valueGetter: (params) => params.row?.receivedByDate || '',
      renderCell: (params) => (
        <span>{params.value ? new Date(params.value).toLocaleDateString() : '—'}</span>
      ),
    },
    { field: 'status', headerName: 'Status', width: 140, type: 'singleSelect', valueOptions: STATUS_OPTIONS, renderCell: (params) => (
      <span style={statusStyle(params.value)}>{params.value}</span>
    ) },
    {
      field: 'total',
      headerName: 'Total',
      width: 140,
      valueGetter: (params) => currency(params.value),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      width: 350,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Button
            style={{ color: 'white', backgroundColor: 'rgb(25, 118, 210)' }}
            onClick={(e) => {
              e.stopPropagation()
              handleEdit(params.row)
            }}
            variant="contained"
            size="small"
          >
            Edit
          </Button>
          <Button
            style={{ color: 'white', backgroundColor: viewButtonColor(params.row) }}
            variant="contained"
            size="small"
            onClick={(e) => { e.stopPropagation(); history.push(`/orders/${params.row._id}`) }}
          >
            View
          </Button>
          <Button
            style={{
              color: 'white',
              backgroundColor: params.row.invoiceDeleted ? '#c62828' : '#555',
            }}
            variant="contained"
            size="small"
            disabled={!params.row.invoiceId || params.row.invoiceDeleted}
            onClick={() => params.row.invoiceId && !params.row.invoiceDeleted && window.open(`/invoice/${params.row.invoiceId}`, '_blank')}
          >
            {params.row.invoiceDeleted ? 'Deleted Invoice' : 'Open Invoice'}
          </Button>
          <Button
            style={{ color: 'white', backgroundColor: '#c62828' }}
            variant="contained"
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              handleDeleteClick(params.row)
            }}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ]

  const selectedOrders = useMemo(
    () => orders.filter((o) => rowSelectionModel.includes(o._id)),
    [orders, rowSelectionModel]
  )

  const paymentBalance = (order) => {
    const paid = paymentTotal(order?.payments)
    const total = Number(order?.total) || 0
    const remaining = total - paid
    return remaining < 0 ? 0 : remaining
  }

  const handleApplyStatus = () => {
    if (!selectedOrders.length) return
    if (bulkStatus === 'Paid') {
      setConfirmOrders(selectedOrders)
      setConfirmOpen(true)
      return
    }
    if (bulkStatus === 'Booked') {
      setConfirmOrders(selectedOrders)
      setConfirmBookedOpen(true)
      return
    }
    selectedOrders.forEach((order) => {
      dispatch(updateOrder(order._id, { status: bulkStatus }, openSnackbar))
    })
  }

  const handleConfirmPaid = () => {
    const today = todayIso()
    confirmOrders.forEach((order) => {
      const remaining = paymentBalance(order)
      const newPayment = {
        amountPaid: remaining,
        datePaid: today,
        paymentMethod: 'Check',
        note: 'Bulk Updated',
        paidBy: auth?.result?.email || auth?.result?.name || 'Current User',
      }
      const nextPayments = remaining > 0 ? [...(order.payments || []), newPayment] : (order.payments || [])
      dispatch(updateOrder(order._id, { status: 'Paid', payments: nextPayments }, openSnackbar))
    })
    setConfirmOpen(false)
    setConfirmOrders([])
  }

  const handleConfirmBooked = () => {
    confirmOrders.forEach((order) => {
      dispatch(updateOrder(order._id, { status: 'Booked' }, openSnackbar))
    })
    setConfirmBookedOpen(false)
    setConfirmOrders([])
  }

  return (
    <div className={styles.pageLayout}>
      <Container style={{ width: '90%', maxWidth: 1400 }}>
        <div className={styles.buttons}>
          <h1>Orders</h1>
          <Box display="flex" alignItems="center" style={{ gap: 12, flexWrap: 'wrap' }}>
            <TextField
              select
              label="Status"
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              size="small"
              variant="outlined"
              style={{ minWidth: 160 }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              color="primary"
              disabled={!selectedOrders.length}
              onClick={handleApplyStatus}
            >
              Update Selected
            </Button>
          </Box>
        </div>
        <div style={{ width: '100%', overflowX: 'auto' }}>
          <div style={{ minWidth: 1150 }}>
            <DataGrid
              autoHeight
              loading={isLoading}
              rows={rows}
              getRowId={(row) => row._id}
              columns={columns}
              pageSize={25}
              rowsPerPageOptions={[10, 25, 50, 100]}
              components={{ Toolbar: GridToolbar }}
              componentsProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } } }}
              sx={{
                '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '6px' },
                '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '8px' },
                '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '12px' },
              }}
              onRowDoubleClick={(params) => handleEdit(params.row)}
              checkboxSelection
              disableRowSelectionOnClick
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={(model) => setRowSelectionModel(model)}
            />
          </div>
        </div>
      </Container>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Order</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={16}>
            <TextField
              select
              label="Status"
              value={formData.status}
              onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
              style={{ marginBottom: 16 }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Link"
              value={formData.link}
              onChange={(e) => setFormData((prev) => ({ ...prev, link: e.target.value }))}
              fullWidth
              style={{ marginBottom: 16 }}
            />
            <Typography variant="subtitle1" gutterBottom>Payments</Typography>
            {(formData.payments || []).map((p, idx) => (
              <Box key={idx} display="grid" gridTemplateColumns="repeat(auto-fit,minmax(140px,1fr))" gridGap={8} alignItems="center" style={{ marginBottom: 8 }}>
                <TextField
                  label="Amount"
                  type="number"
                  value={p.amountPaid || ''}
                  onChange={(e) => handlePaymentChange(idx, 'amountPaid', e.target.value)}
                />
                <TextField
                  label="Date"
                  type="date"
                  value={p.datePaid ? new Date(p.datePaid).toISOString().slice(0, 10) : ''}
                  onChange={(e) => handlePaymentChange(idx, 'datePaid', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  select
                  label="Method"
                  value={p.paymentMethod || 'Check'}
                  onChange={(e) => handlePaymentChange(idx, 'paymentMethod', e.target.value)}
                >
                  {PAYMENT_METHODS.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Note"
                  value={p.note || ''}
                  onChange={(e) => handlePaymentChange(idx, 'note', e.target.value)}
                />
                <TextField
                  label="Paid By"
                  value={p.paidBy || ''}
                  onChange={(e) => handlePaymentChange(idx, 'paidBy', e.target.value)}
                />
                <Button onClick={() => handleRemovePayment(idx)} color="secondary">Remove</Button>
              </Box>
            ))}
            <Button onClick={handleAddPayment} variant="outlined" color="primary" style={{ marginBottom: 8 }}>
              Add Payment
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} color="primary" variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Confirm Payment</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            You are marking these orders as Paid. Please confirm the totals:
          </Typography>
          <Box display="flex" flexDirection="column" style={{ gap: 6, marginTop: 8 }}>
            {confirmOrders.map((order) => {
              const remaining = paymentBalance(order)
              return (
                <Box key={order._id} display="flex" justifyContent="space-between">
                  <span>{order.invoiceNumber || 'Invoice'}</span>
                  <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <strong>Total: {currency(order.total)}</strong>
                    <span>Remainder: {currency(remaining)}</span>
                  </span>
                </Box>
              )
            })}
          </Box>
          <Box display="flex" justifyContent="space-between" style={{ marginTop: 12, borderTop: '1px solid #eee', paddingTop: 8 }}>
            <Typography variant="subtitle1">Total Paying</Typography>
            <Typography variant="subtitle1">
              {currency(confirmOrders.reduce((sum, o) => sum + (paymentBalance(o)), 0))}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmPaid} color="primary" variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmBookedOpen} onClose={() => setConfirmBookedOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Confirm Booked</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            You are marking these orders as Booked. Please confirm the list:
          </Typography>
          <Box display="flex" flexDirection="column" style={{ gap: 6, marginTop: 8 }}>
            {confirmOrders.map((order) => (
              <Box key={order._id} display="flex" justifyContent="space-between">
                <span>{order.invoiceNumber || 'Invoice'}</span>
                <span>{order.receivedByDate ? new Date(order.receivedByDate).toLocaleDateString() : '—'}</span>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBookedOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmBooked} color="primary" variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={handleCancelDelete} fullWidth maxWidth="xs">
        <DialogTitle>Delete Order</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete this order?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {deleteTarget?.invoiceNumber ? `Invoice #${deleteTarget.invoiceNumber}` : 'This order'} will be moved to Recently Deleted. The linked invoice will not be deleted.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="secondary" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default Orders
